import axios from 'axios';
import apiUrl from '../../config/api';

const authExpiredEvent = 'quizify:auth-expired';
const impersonationReturnTokenKey = 'quizify.impersonation.returnToken';
let refreshPromise = null;

axios.defaults.withCredentials = true;

export const getStoredAccessToken = () => (
  sessionStorage.getItem('token') || localStorage.getItem('token')
);

export const getStoredAccessTokenSource = () => {
  if (sessionStorage.getItem('token')) {
    return 'session';
  }

  return localStorage.getItem('token') ? 'local' : null;
};

export const storeAccessToken = (token) => {
  localStorage.removeItem('token');

  if (token) {
    sessionStorage.setItem('token', token);
  } else {
    sessionStorage.removeItem('token');
  }
};

export const clearAccessToken = () => {
  localStorage.removeItem('token');
  sessionStorage.removeItem('token');
};

export const getStoredAccessTokenUser = () => {
  const token = getStoredAccessToken();

  if (!token) {
    return null;
  }

  try {
    const encodedPayload = token.split('.')[1];
    const normalizedPayload = encodedPayload.replace(/-/g, '+').replace(/_/g, '/');
    const paddedPayload = normalizedPayload.padEnd(Math.ceil(normalizedPayload.length / 4) * 4, '=');
    const payload = JSON.parse(window.atob(paddedPayload));

    return {
      id: payload.id,
      firstName: payload.firstName || '',
      lastName: payload.lastName || '',
      email: payload.email || '',
      role: payload.role || '',
    };
  } catch (error) {
    return null;
  }
};

export const isDefinitiveSessionFailure = (error) => (
  error?.response?.status === 401
  && String(error?.config?.url || '').includes('/auth/refresh')
);

const isApiRequest = (config = {}) => {
  const url = String(config.url || '');
  return url.startsWith(apiUrl) || url.startsWith('/api/');
};

const setAuthorizationHeader = (config, token) => {
  config.headers = config.headers || {};

  if (typeof config.headers.set === 'function') {
    if (token) {
      config.headers.set('Authorization', `Bearer ${token}`);
    } else {
      config.headers.delete?.('Authorization');
    }
    return;
  }

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  } else {
    delete config.headers.Authorization;
    delete config.headers.authorization;
  }
};

const notifyAuthExpired = () => {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(authExpiredEvent));
  }
};

export const refreshAccessToken = () => {
  if (sessionStorage.getItem(impersonationReturnTokenKey)) {
    return Promise.reject(new Error('La sessione impersonata non può essere rinnovata.'));
  }

  if (!refreshPromise) {
    refreshPromise = axios.post(`${apiUrl}/auth/refresh`, null, {
      withCredentials: true,
      skipAuthRefresh: true,
      skipAuthToken: true,
    })
      .then((response) => {
        const accessToken = response.data?.accessToken || response.data?.token;

        if (!accessToken) {
          throw new Error('Il server non ha restituito un access token.');
        }

        storeAccessToken(accessToken);
        return { accessToken, user: response.data.user };
      })
      .finally(() => {
        refreshPromise = null;
      });
  }

  return refreshPromise;
};

export const installAuthInterceptors = () => {
  const requestInterceptor = axios.interceptors.request.use((config) => {
    config.withCredentials = true;

    if (isApiRequest(config) && !config.skipAuthToken) {
      setAuthorizationHeader(config, getStoredAccessToken());
    }

    return config;
  });

  const responseInterceptor = axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config || {};
      const retriedRequestStillUnauthorized = error.response?.status === 401
        && isApiRequest(originalRequest)
        && originalRequest.authRetry;

      if (retriedRequestStillUnauthorized) {
        return Promise.reject(error);
      }

      const shouldTryRefresh = error.response?.status === 401
        && isApiRequest(originalRequest)
        && !originalRequest.skipAuthRefresh
        && !originalRequest.authRetry;

      if (!shouldTryRefresh) {
        return Promise.reject(error);
      }

      originalRequest.authRetry = true;

      try {
        const { accessToken } = await refreshAccessToken();
        setAuthorizationHeader(originalRequest, accessToken);
        return axios(originalRequest);
      } catch (refreshError) {
        if (isDefinitiveSessionFailure(refreshError)) {
          clearAccessToken();
          notifyAuthExpired();
        }

        return Promise.reject(refreshError);
      }
    }
  );

  return () => {
    axios.interceptors.request.eject(requestInterceptor);
    axios.interceptors.response.eject(responseInterceptor);
  };
};

export const logoutServerSession = () => axios.post(`${apiUrl}/auth/logout`, null, {
  withCredentials: true,
  skipAuthRefresh: true,
  skipAuthToken: true,
});

export const AUTH_EXPIRED_EVENT = authExpiredEvent;
